import { atom } from 'jotai';
import { useSetAtom } from 'jotai/react';
import { useCallback, useState, type PointerEvent } from 'react';
import { AIAssistantSettingsStorage } from './AIAssistantSettingsStorage';

const DEFAULT_DRAWER_WIDTH = 420;
const AI_ASSISTANT_DRAWER_MIN_WIDTH = 320;
const AI_ASSISTANT_DRAWER_MAX_WIDTH = 720;

const clampDrawerWidth = (n: number): number => {
  if (n < AI_ASSISTANT_DRAWER_MIN_WIDTH) return AI_ASSISTANT_DRAWER_MIN_WIDTH;
  if (n > AI_ASSISTANT_DRAWER_MAX_WIDTH) return AI_ASSISTANT_DRAWER_MAX_WIDTH;
  return n;
};

const initialDrawerWidth = (): number => {
  const stored = AIAssistantSettingsStorage.getDrawerWidth();
  if (stored === null) return DEFAULT_DRAWER_WIDTH;
  return clampDrawerWidth(stored);
};

export const aiAssistantDrawerOpenAtom = atom<boolean>(false);

const internalDrawerWidthAtom = atom<number>(initialDrawerWidth());

// ドラッグ中は高頻度で更新されるため localStorage への書き込みはここでは行わず、
// useAIAssistantDrawerResize のドラッグ終了時にまとめて永続化する。
export const aiAssistantDrawerWidthAtom = atom(
  (get) => get(internalDrawerWidthAtom),
  (_get, set, next: number) => {
    set(internalDrawerWidthAtom, clampDrawerWidth(next));
  }
);

export const useAIAssistantDrawerResize = () => {
  const setWidth = useSetAtom(aiAssistantDrawerWidthAtom);
  const [isResizing, setIsResizing] = useState(false);

  const onPointerDown = useCallback((e: PointerEvent<HTMLDivElement>) => {
    // PointerEvent はキャプチャを取らないとドラッグ追従できない
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsResizing(true);
  }, []);

  const onPointerMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!isResizing) return;
      // ドロワーは画面右端に張り付いている前提で、左方向のドラッグで幅が広がる
      setWidth(Math.floor(window.innerWidth - e.clientX));
    },
    [isResizing, setWidth]
  );

  const onPointerUp = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      setIsResizing(false);
      const nextWidth = clampDrawerWidth(Math.floor(window.innerWidth - e.clientX));
      setWidth(nextWidth);
      AIAssistantSettingsStorage.setDrawerWidth(nextWidth);
    },
    [setWidth]
  );

  // 中断（OS のジェスチャ奪取等）ではポインタ座標が信頼できないため、
  // 幅の確定・永続化は行わずリサイズ状態だけ解除する
  const onPointerCancel = useCallback((e: PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    setIsResizing(false);
  }, []);

  // 発火時点でキャプチャは既に失われているため解除は不要。
  // 正常終了時は onPointerUp の release 後にも発火するが、その場合は実質 no-op
  const onLostPointerCapture = useCallback(() => {
    setIsResizing(false);
  }, []);

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onLostPointerCapture, isResizing };
};
